import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionMenu, Button, Callout, Select, Text } from '../../../../components';
import { languageName } from '../../../../config/languageEndonyms';
import { runningJobCopy } from '../../seo-copy';
import type { SiteSeoJob } from '../../seo-types';
import { DirtyNote, JobNote, LanguageSlot, PublishBtn, Root, Row } from './SeoHeaderActions.styles';

export type SeoHeaderActionsProps = {
  /** The site's languages; none (an unavailable site) hides the picker. */
  languages: { default: string; all: string[] };
  language: string;
  onLanguage: (code: string) => void;
  dirty: boolean;
  /** The job running now (never one that has ended). */
  publishing: SiteSeoJob | null;
  /** False: the last publish may not have reached the site. */
  confirmed: boolean;
  canRevert: boolean;
  /** The server's issues hold an error: nothing can be published until it is fixed. */
  blocked: boolean;
  /** Why Publish is blocked, already translated (the button's title). */
  blockedReason?: string;
  onPublish: () => void;
  onUndo: () => void;
};

/**
 * The SEO page's header actions: the language picker, the draft or job note, Publish (enabled only with changes,
 * nothing running and nothing blocking), Undo last publish in the menu, and, while the last publish may not have
 * reached the site, a warning with Publish again (the same flow, even without changes).
 */
export const SeoHeaderActions = ({
  languages,
  language,
  onLanguage,
  dirty,
  publishing,
  confirmed,
  canRevert,
  blocked,
  blockedReason,
  onPublish,
  onUndo,
}: SeoHeaderActionsProps): React.ReactElement => {
  const { t } = useTranslation();
  const canPublish = dirty && publishing === null && !blocked;
  const canPublishAgain = publishing === null && !blocked;
  const publish = (): void => {
    if (canPublish) onPublish();
  };
  const publishAgain = (): void => {
    if (canPublishAgain) onPublish();
  };
  return (
    <Root>
      <Row>
        {languages.all.length > 0 ? (
          <LanguageSlot>
            <Select
              label={t('Language')}
              hideLabel
              value={language}
              onChange={onLanguage}
              options={languages.all.map((code) => ({ value: code, label: languageName(code) }))}
            />
          </LanguageSlot>
        ) : null}
        {publishing ? (
          <JobNote text={runningJobCopy(t, publishing)} raw tag="span" />
        ) : dirty ? (
          <DirtyNote text="Draft · not published" tag="span" />
        ) : null}
        <PublishBtn
          type="button"
          aria-disabled={!canPublish}
          title={blocked && blockedReason ? blockedReason : undefined}
          onClick={publish}
        >
          <Text text="Publish" />
        </PublishBtn>
        <ActionMenu
          items={[
            {
              label: t('Undo last publish'),
              disabled: !canRevert || publishing !== null,
              onSelect: onUndo,
            },
          ]}
        />
      </Row>
      {!confirmed && publishing === null ? (
        <Callout
          tone="warning"
          title={t('Not on your site yet')}
          text="Your last publish may not have reached your site."
          action={
            <Button label={t('Publish again')} variant="outline" disabled={!canPublishAgain} onClick={publishAgain} />
          }
        />
      ) : null}
    </Root>
  );
};

export default SeoHeaderActions;
